<script lang="ts">
export type {
    BaseKpiPassThrough,
    BaseKpiPassThroughKey,
} from "./baseKpiPassThrough"
</script>

<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, useSlots } from "vue"
import type { BaseKpiPassThrough } from "./baseKpiPassThrough"
import { mergeBaseKpiPt } from "./baseKpiPassThrough"

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    /**
     * Libellé du KPI — rendu dans le bloc titre si le slot `#title` n’est pas fourni.
     */
    label: string
    /**
     * Valeur affichée — rendue dans le bloc valeur si le slot `#value` n’est pas fourni.
     */
    value: string | number
    /**
     * Clé/type du KPI, émise au clic pour identification.
     */
    type: string
    /**
     * PassThrough Tailwind (fusion additive avec les défauts), par bloc : root, title, valueRow, icon, value, subtitle, footer.
     */
    pt?: BaseKpiPassThrough
    /**
     * Classe d’icône PrimeIcons (ex. `pi pi-list`), à gauche de la valeur (mode défaut sans slot `#value`).
     */
    icon?: string
    /**
     * Couleur de l’icône.
     */
    iconColor?: string
    /**
     * Met le KPI en surbrillance (filtre actif).
     */
    highlight?: boolean
    /**
     * Indique si le KPI est cliquable.
     */
    clickable?: boolean
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    icon: undefined,
    highlight: false,
    clickable: true,
    iconColor: undefined,
    pt: undefined,
})

const emit = defineEmits<{
    click: [type: string]
}>()

const slots = useSlots()

// ================================
// DONNÉES CALCULÉES
// ================================

const getMergedPt = computed(() => mergeBaseKpiPt(props.pt))

const getRootClass = computed(() => {
    const base = getMergedPt.value.root
    const interactive = props.clickable
        ? "cursor-pointer hover:bg-[#F5F7FB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400"
        : "cursor-default"
    const hi = props.highlight
        ? props.clickable
            ? "shadow-[0_0_4px_#31AC84] hover:shadow-[0_0_8px_#31AC84]"
            : "shadow-[0_0_4px_#31AC84]"
        : ""
    return [base, interactive, hi].filter(Boolean).join(" ")
})

// ================================
// INTERACTIONS
// ================================

/**
 * Émet le clic avec le type du KPI.
 */
function onClick(): void {
    emit("click", props.type)
}

/**
 * Clic sur la racine : émet seulement si la carte est interactive.
 */
function onRootClick(): void {
    if (props.clickable) {
        onClick()
    }
}

/**
 * Raccourcis clavier uniquement si la carte est interactive.
 */
function onRootKeydown(): void {
    if (props.clickable) {
        onClick()
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- KPI — PassThrough type PrimeVue (blocs root / title / valeur / …) -->
    <!-- ================================ -->
    <div
        :class="getRootClass"
        :role="clickable ? 'button' : undefined"
        :tabindex="clickable ? 0 : undefined"
        :aria-label="label"
        @click="onRootClick"
        @keydown.enter.prevent="onRootKeydown"
        @keydown.space.prevent="onRootKeydown"
    >
        <div :class="getMergedPt.title">
            <slot name="title">
                <span class="block truncate">{{ label }}</span>
            </slot>
        </div>

        <div :class="getMergedPt.valueRow">
            <slot name="value">
                <BaseIcon
                    v-if="icon"
                    :class="getMergedPt.icon"
                    :icon="icon"
                    size="1rem"
                    :color="iconColor ?? 'var(--p-primary-600)'"
                />
                <div
                    v-if="value !== undefined && value !== null"
                    :class="getMergedPt.value"
                >
                    {{ value }}
                </div>
            </slot>
        </div>

        <div v-if="slots.subtitle" :class="getMergedPt.subtitle">
            <slot name="subtitle" />
        </div>

        <div v-if="slots.footer" :class="getMergedPt.footer">
            <slot name="footer" />
        </div>
    </div>
</template>
